import { GardenPlant } from "@/components/client/Garden";
import { useCallback, useEffect, useState } from "react";
import { Heart, Trophy, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TIER_META } from "@/lib/rubric";
import { SampleTag } from "@/components/community/SampleTag";

function fmtRange(r) {
  const f = (s) => new Date(s + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${f(r.start)} – ${f(r.end)}`;
}

function MiniCard({ item, onCheer, onRemove }) {
  const c = item.card;
  const total = Math.max(1, c.checkins);
  const max = Math.max(1, ...c.days.map((d) => d.count));
  return (
    <li className="rounded-3xl bg-stone-900 text-stone-100 p-5 flex flex-col gap-4" data-testid={`wins-feed-item-${item.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Week in habits</div>
          <div className="font-display text-xl font-extrabold mt-0.5 flex items-center gap-2" data-testid={`wins-feed-handle-${item.id}`}>{item.mine ? "You" : item.handle}{item.is_sample && <SampleTag id={`wins-${item.id}`} />}</div>
          <div className="text-xs text-stone-400">{fmtRange(c.range)} · {c.level}</div>
        </div>
        {item.mine && <button onClick={() => onRemove(item)} data-testid={`wins-feed-remove-${item.id}`} className="text-stone-500 hover:text-red-300" title="Remove from feed"><Trash2 className="w-4 h-4" /></button>}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[[c.checkins, "check-ins"], [c.active_days, "days"], [c.best_streak, "streak"], [c.points, "pts"]].map(([v, l]) => (
          <div key={l} className="rounded-2xl bg-white/5 px-2 py-2 text-center"><div className="font-mono text-lg font-bold text-amber-300">{v}</div><div className="text-[10px] text-stone-400">{l}</div></div>
        ))}
      </div>
      <div className="flex items-end gap-1 h-10">
        {c.days.map((d) => <div key={d.date} className={`flex-1 rounded-md ${d.count ? "bg-amber-400" : "bg-white/10"}`} style={{ height: `${Math.max(12, (d.count / max) * 100)}%` }} title={`${d.date}: ${d.count}`} />)}
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-white/10">
        {["full", "partial", "showed_up"].map((k) => c.tiers[k] > 0 && <div key={k} className={TIER_META[k].dot} style={{ width: `${(c.tiers[k] / total) * 100}%` }} />)}
      </div>
      <div className="flex items-center justify-between gap-2 mt-auto">
        <span className="text-xs text-stone-400" data-testid={`wins-feed-cheers-${item.id}`}>{item.cheer_count} cheer{item.cheer_count === 1 ? "" : "s"}</span>
        {!item.mine && (
          <button onClick={() => onCheer(item)} disabled={item.cheered} data-testid={`wins-feed-cheer-${item.id}`}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full transition active:scale-95 ${item.cheered ? "bg-rose-500/20 text-rose-200 cursor-default" : "bg-amber-400 text-stone-900 hover:bg-amber-300"}`}>
            <Heart className={`w-3.5 h-3.5 ${item.cheered ? "fill-rose-300" : ""}`} /> {item.cheered ? "Cheered" : "Cheer"}
          </button>
        )}
      </div>
    </li>
  );
}

function MilestoneItem({ item, onCheer, onRemove }) {
  const c = item.card;
  return (
    <li className="rounded-3xl bg-stone-900 text-white p-5 flex flex-col gap-3" data-testid={`wins-feed-milestone-${item.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Garden milestone</div>
          <div className="font-display text-xl font-extrabold mt-0.5 flex items-center gap-2" data-testid={`wins-feed-handle-${item.id}`}>{item.mine ? "You" : item.handle}{item.is_sample && <SampleTag id={`wins-${item.id}`} />}</div>
          <div className="text-xs text-stone-400">grew into <span className="text-amber-200 font-semibold">{c.level}</span> · level {c.level_index} of 10</div>
        </div>
        {item.mine && <button onClick={() => onRemove(item)} data-testid={`wins-feed-remove-${item.id}`} className="text-stone-500 hover:text-red-300" title="Remove from feed"><Trash2 className="w-4 h-4" /></button>}
      </div>
      <div className="rounded-2xl bg-white/5 grid place-items-center py-2"><GardenPlant stage={c.stage} progress={1} className="w-24 h-32" animate={false} /></div>
      <div className="flex items-center justify-between gap-2 mt-auto">
        <span className="text-xs text-stone-400" data-testid={`wins-feed-cheers-${item.id}`}>{item.cheer_count} cheer{item.cheer_count === 1 ? "" : "s"}</span>
        {!item.mine && (
          <button onClick={() => onCheer(item)} disabled={item.cheered} data-testid={`wins-feed-cheer-${item.id}`}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full transition active:scale-95 ${item.cheered ? "bg-rose-500/20 text-rose-200 cursor-default" : "bg-amber-400 text-stone-900 hover:bg-amber-300"}`}>
            <Heart className={`w-3.5 h-3.5 ${item.cheered ? "fill-rose-300" : ""}`} /> {item.cheered ? "Cheered" : "Cheer"}
          </button>
        )}
      </div>
    </li>
  );
}

export function WinsFeed() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const r = await api.get("/wins/feed");
    setData(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cheer = async (item) => {
    setData((d) => ({ ...d, items: d.items.map((i) => (i.id === item.id ? { ...i, cheered: true, cheer_count: i.cheer_count + 1 } : i)) }));
    try {
      await api.post(`/wins/${item.id}/cheer`);
      toast.success(`Cheered ${item.handle}'s week.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cheer just now");
      load();
    }
  };
  const remove = async (item) => {
    await api.delete(`/wins/share/${item.id}`);
    toast("Removed from the feed.");
    load();
  };

  if (!data || data.reason) return null;
  return (
    <section className="warm-card p-6" data-testid="wins-feed">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-amber-700" />
        <h3 className="font-display font-bold text-stone-900">This week's wins</h3>
        <span className="text-xs text-stone-500">· cards shared by members of your categories · one tap to cheer</span>
      </div>
      {data.items.length === 0 ? (
        <p className="text-sm text-stone-500 mt-3" data-testid="wins-feed-empty">No cards yet. Share yours from the Weekly Wins card on your dashboard — counts and tiers only, never habit names.</p>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {data.items.map((i) => (i.kind === "milestone" ? <MilestoneItem key={i.id} item={i} onCheer={cheer} onRemove={remove} /> : <MiniCard key={i.id} item={i} onCheer={cheer} onRemove={remove} />))}
        </ul>
      )}
    </section>
  );
}
